// useChangeFieldForm.ts
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { AnyObject, ObjectSchema } from 'yup';

type FieldType<T extends string, V> = Record<T, V>;

export const useChangeFieldForm = <T extends string, V = string>(
  initialValue: V | null | undefined,
  fieldName: T,
  validationSchema: ObjectSchema<FieldType<T, V>, AnyObject, any, ''> | null,
  serviceMethod: (value: V) => Promise<unknown>, // Изменили тип возвращаемого значения
  errorMessage: string,
  cb?: () => void,
) => {
  const { t } = useTranslation();

  const {
    values,
    errors,
    handleSubmit,
    handleChange,
    touched,
    setFieldTouched,
    isSubmitting,
    setFieldError,
    setFieldValue,
    submitForm,
  } = useFormik<FieldType<T, V>>({
    initialValues: { [fieldName]: initialValue } as FieldType<T, V>,
    validationSchema: validationSchema || null,
    onSubmit: async (values) => {
      const value = values[fieldName];
      try {
        await serviceMethod(value);
        cb?.();
      } catch (error) {
        if (error instanceof AxiosError && error.status === 409) {
          setFieldError(fieldName, t(errorMessage));
        }
      }
    },
    enableReinitialize: true,
  });

  const checkDisableButton = () => {
    if (values[fieldName] === initialValue) return true;
    return !!errors[fieldName] || isSubmitting;
  };

  return {
    values,
    errors,
    handleSubmit,
    handleChange,
    touched,
    setFieldTouched,
    isSubmitting,
    setFieldValue,
    checkDisableButton,
    submitForm,
  };
};
